import { useMemo } from "react";

/**
 * ResponsiveImg — drop-in <img> replacement that emits a `srcset` so the
 * browser pulls only the variant it needs. Pairs with the backend `?w=`
 * query on `/api/files/*` and `/api/img/item/*`.
 *
 * Usage:
 *   <ResponsiveImg src={item.photo_urls[0]} alt={item.name} sizes="(min-width:768px) 25vw, 50vw" />
 *
 * Falls back to a plain <img> when:
 *   - src is empty / a data URL / an external http(s) URL
 *   - srcset can't be inferred (non-app URL)
 *
 * The same widths are configured server-side in _IMG_WIDTHS.
 */

const WIDTHS = [320, 480, 720, 960, 1280, 1600];
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function resolveBase(src) {
    if (!src) return null;
    if (src.startsWith("data:")) return null;
    // External absolute URLs (e.g. https://cdn...) — can't add ?w
    if (/^https?:\/\//i.test(src) && !src.startsWith(BACKEND)) return null;
    if (src.startsWith("/api/")) return `${BACKEND}${src}`;
    if (src.startsWith(BACKEND)) return src;
    return null;
}

function buildSrcSet(base) {
    const sep = base.includes("?") ? "&" : "?";
    return WIDTHS.map(w => `${base}${sep}w=${w} ${w}w`).join(", ");
}

export default function ResponsiveImg({ src, alt = "", sizes, className, loading = "lazy", onError, ...rest }) {
    const { base, srcSet, defaultSrc } = useMemo(() => {
        const resolved = resolveBase(src);
        if (!resolved) return { base: null, srcSet: null, defaultSrc: src };
        return {
            base: resolved,
            srcSet: buildSrcSet(resolved),
            // Pick a sensible mid-tier default for browsers that ignore srcset
            defaultSrc: `${resolved}${resolved.includes("?") ? "&" : "?"}w=720`,
        };
    }, [src]);

    if (!base) {
        return <img src={src || "/default-item.svg"} alt={alt} className={className} loading={loading} onError={onError} {...rest} />;
    }

    return (
        <img
            src={defaultSrc}
            srcSet={srcSet}
            sizes={sizes || "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"}
            alt={alt}
            className={className}
            loading={loading}
            decoding="async"
            onError={onError}
            {...rest}
        />
    );
}
